<main>
  <ng-container *transloco="let t">
    <section class="wallpaper"></section>

    <section class="login">
      <!-- Success state -->
      @if (store.success()) {
        <div class="state-wrapper">
          <div class="logo-wrapper"><p class="logo"></p></div>

          <div class="icon-circle success-icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
            >
              <path
                d="M5 12l5 5 9-9"
                stroke="#ffffff"
                stroke-width="2.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </div>

          <h2 class="state-title">{{ t('Password reset successful') }}</h2>

          <p class="description">
            {{
              t(
                'Your password has been reset successfully. You can now sign in with your new password.'
              )
            }}
          </p>

          <a routerLink="/login" class="open-app-btn">{{ t('Go to Login') }}</a>
        </div>
      }

      <!-- Form state -->
      @if (!store.success()) {
        <div class="form-wrapper">
          <div class="logo-wrapper">
            <p class="logo"></p>
          </div>
          <div class="welcome-text">
            <h2>{{ t('Reset Password') }}</h2>
            <p>
              {{
                t(
                  'Enter your new password and confirm it to complete the change.'
                )
              }}
            </p>
          </div>

          @if (store.error()) {
            <div class="error-banner">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="#f04438"
                  stroke-width="1.5"
                />
                <path
                  d="M12 8v4M12 16h.01"
                  stroke="#f04438"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <span>{{ store.error() }}</span>
            </div>
          }

          <form [formGroup]="form" (ngSubmit)="resetPassword()">
            <div class="sm:mx-auto sm:w-full sm:max-w-sm flex flex-col gap-4">
              <!-- New password -->
              <div>
                <label
                  for="passwordNew"
                  class="block text-sm font-medium leading-6 text-gray-900"
                  >{{ t('New Password') }}</label
                >
                <div class="relative mt-2 rounded-md shadow-sm">
                  <div
                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <span class="icon password-icon"></span>
                  </div>
                  <input
                    formControlName="passwordNew"
                    matInput
                    id="passwordNew"
                    [type]="showPassword() ? 'text' : 'password'"
                    class="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  <button
                    type="button"
                    [attr.aria-label]="
                      showPassword() ? t('Hide password') : t('Show password')
                    "
                    [attr.aria-pressed]="showPassword()"
                    class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer border-none bg-transparent"
                    (click)="showPassword.set(!showPassword())"
                  >
                    <span
                      class="icon"
                      [ngClass]="
                        showPassword() ? 'eye-off-grey-icon' : 'eye-grey-icon'
                      "
                    ></span>
                  </button>
                </div>

                @if (
                  showValidationErrors() &&
                  form.controls['passwordNew'].hasError('required')
                ) {
                  <mat-error>{{ t('Password is required') }}</mat-error>
                }

                @if (
                  form.controls['passwordNew'].touched ||
                  form.controls['passwordNew'].dirty
                ) {
                  <div class="password-hints">
                    <div class="hint" [class.hint-ok]="passwordChecks().length">
                      <span class="hint-dot"></span
                      >{{ t('At least 8 characters') }}
                    </div>
                    <div class="hint" [class.hint-ok]="passwordChecks().upper">
                      <span class="hint-dot"></span
                      >{{ t('One uppercase letter (A–Z)') }}
                    </div>
                    <div class="hint" [class.hint-ok]="passwordChecks().lower">
                      <span class="hint-dot"></span
                      >{{ t('One lowercase letter (a–z)') }}
                    </div>
                    <div class="hint" [class.hint-ok]="passwordChecks().number">
                      <span class="hint-dot"></span>{{ t('One number (0–9)') }}
                    </div>
                    <div
                      class="hint"
                      [class.hint-ok]="passwordChecks().special"
                    >
                      <span class="hint-dot"></span
                      >{{ t('One special character (!@#$...)') }}
                    </div>
                  </div>
                }
              </div>

              <!-- Confirm new password -->
              <div>
                <label
                  for="passwordNew2"
                  class="block text-sm font-medium leading-6 text-gray-900"
                  >{{ t('Confirm New Password') }}</label
                >
                <div class="relative mt-2 rounded-md shadow-sm">
                  <div
                    class="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3"
                  >
                    <span class="icon password-icon"></span>
                  </div>
                  <input
                    formControlName="passwordNew2"
                    matInput
                    id="passwordNew2"
                    [type]="showPassword2() ? 'text' : 'password'"
                    class="block w-full rounded-md border-0 py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                  />
                  <button
                    type="button"
                    [attr.aria-label]="
                      showPassword2() ? t('Hide password') : t('Show password')
                    "
                    [attr.aria-pressed]="showPassword2()"
                    class="absolute inset-y-0 right-0 flex items-center pr-3 cursor-pointer border-none bg-transparent"
                    (click)="showPassword2.set(!showPassword2())"
                  >
                    <span
                      class="icon"
                      [ngClass]="
                        showPassword2() ? 'eye-off-grey-icon' : 'eye-grey-icon'
                      "
                    ></span>
                  </button>
                </div>

                @if (
                  showValidationErrors() &&
                  form.controls['passwordNew2'].hasError('required')
                ) {
                  <mat-error>{{
                    t('Password confirmation is required')
                  }}</mat-error>
                }
                @if (
                  showValidationErrors() && form.hasError('passwordsDontMatch')
                ) {
                  <mat-error>{{ t('Passwords do not match') }}</mat-error>
                }
              </div>

              <div>
                <button
                  mat-flat-button
                  class="primary-btn"
                  type="submit"
                  [disabled]="store.status() === status.Loading"
                >
                  @if (store.status() !== status.Loading) {
                    {{ t('Change password') }}
                  } @else {
                    <span
                      class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                    ></span>
                    {{ t('Changing...') }}
                  }
                </button>
              </div>
            </div>
          </form>
        </div>
      }
    </section>
  </ng-container>
</main>
